import React, { useState, useEffect } from 'react';
import { HiSearch, HiTrash } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './DeletedData.css';

const DeletedProducts = () => {
  const { user } = useAuth();
  const { effectiveBranchId } = useBranch();
  const { error } = useNotification();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || '');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then(res => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    const businessId = user?.role === 'super_admin' ? selectedBusinessId : user?.business_id;
    if (!businessId) {
      setLoading(false);
      setProducts([]);
      return;
    }
    setLoading(true);
    const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
    axios.get(`${API}/products/business/${businessId}/deleted${params}`)
      .then(res => setProducts(res.data.products || []))
      .catch(err => error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedBusinessId, user?.role, user?.business_id, effectiveBranchId]);

  const filtered = products.filter(p =>
    !searchTerm ||
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.product_code || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="deleted-data-page">
        <div className="page-header">
          <div>
            <h1><HiTrash className="page-icon" /> Deleted Products & Variants</h1>
            <p className="page-subtitle">View soft-deleted products and their variants</p>
          </div>
        </div>
        <div className="filters-container">
          {user?.role === 'super_admin' && (
            <div className="filter-dropdown">
              <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)}>
                <option value="">Select Business</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              placeholder="Search by name or product code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <Loader message="Loading deleted products..." variant="block" />
        ) : (
          <div className="table-container">
            {filtered.length === 0 ? (
              <div className="empty-state">
                <p>No deleted products found.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Variants</th>
                    <th>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <React.Fragment key={p.id}>
                      <tr>
                        <td className="font-mono">{p.product_code || '—'}</td>
                        <td className="font-semibold">{p.name}</td>
                        <td><span className={`type-badge ${p.product_type}`}>{p.product_type || '—'}</span></td>
                        <td>
                          {p.variants?.length ? (
                            <div className="variants-list-inline">
                              {p.variants.map(v => (
                                <span key={v.id} className="variant-chip">
                                  {[v.color, v.size].filter(Boolean).join(' / ') || '—'}
                                  {v.deleted_at && <span className="deleted-badge">deleted</span>}
                                </span>
                              ))}
                            </div>
                          ) : '—'}
                        </td>
                        <td>{formatDate(p.deleted_at)}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DeletedProducts;
